// 영웅 4명과 5레벨마다 새 스킬(정본: docs/CONTENT-90D-PRD.md 3·3A). 데이터만 — 스킬 타임라인은 src/shared/skills/(c005~).
import type { HeroDef, HeroId, SkillProp, SkillUnlock } from './types';

export const LEVEL_MAX_90D = 99;
/** 새 스킬이 열리는 레벨: 1(기본 3) + 5..95 5레벨마다 */
export const SKILL_LEVELS: readonly number[] = [1, ...Array.from({ length: 19 }, (_, i) => (i + 1) * 5)];
export const JOB_LEVEL = 30;
export const REBIRTH_LEVEL = 99;
/** 모바일 스킬 슬롯: 6칸 × 2페이지 + 각성 1 */
export const SKILL_SLOTS = { perPage: 6, pages: 2 } as const;

type Row = readonly [level: number, name: string, kind: SkillUnlock['kind'], props: readonly SkillProp[]];
/** 전직 갈래별 스킬 이름: [갈래][전직 I(30) · 전직 II(40) · 2차 각성(50)] (PRD 3A.1 전직 스킬) */
type JobNames = readonly [readonly [string, string, string], readonly [string, string, string]];
const JOB_SLOT: Readonly<Record<number, 0 | 1 | 2>> = { 30: 0, 40: 1, 50: 2 };
/**
 * 표 → SkillUnlock. 고정 ID를 붙이고, 전직(30·40)·2차 각성(50) 줄은 두 갈래로 나눠 이름을 갈래별로 채운다.
 * ID 규칙은 types.SkillUnlock.id 주석.
 */
function rows(hero: HeroId, jobs: JobNames, r: readonly Row[]): SkillUnlock[] {
  const out: SkillUnlock[] = [];
  let base = 0;
  for (const [level, name, kind, props] of r) {
    if (kind === 'base') { out.push({ id: `${hero}.base.${base++}`, level, name, kind, props }); continue; }
    if (kind === 'job' || kind === 'awaken2') {
      const k = JOB_SLOT[level];
      for (const j of [0, 1] as const) out.push({ id: `${hero}.${kind}${level}.${j === 0 ? 'a' : 'b'}`, level, name: jobs[j][k], kind, props, job: j });
      continue;
    }
    out.push({ id: `${hero}.${kind}${level}`, level, name, kind, props });
  }
  return out;
}

export const HEROES_90D: readonly HeroDef[] = [
  {
    id: 'kael', name: '카엘', job: '검사', weapon: '대검', role: '근접 딜·몹몰이', colors: ['#e0313c', '#e8b04a'],
    awaken: '진홍 초승달 난무', jobs: ['블러드 버서커', '소드 마스터'],
    skills: rows('kael', [['피의 폭주', '흡혈 참격', '블러드 아포칼립스'], ['검기 방출', '천검 낙하', '천검무']], [
      [1, '피의 돌진', 'base', []], [1, '칼날 폭풍', 'base', ['pull']], [1, '붉은 낙화', 'base', ['downHit']],
      [5, '천공 올려베기', 'active', ['launcher']], [10, '진홍 초승달', 'active', ['projectile']], [15, '공중 난무', 'active', ['air']],
      [20, '대지 가르기', 'active', ['downHit']], [25, '혈검 회전베기', 'active', ['superArmor']], [30, '전직 스킬 I', 'job', []],
      [35, '잡아던지기', 'active', ['grab']], [40, '전직 스킬 II', 'job', []], [45, '진홍 소용돌이', 'active', ['pull']],
      [50, '2차 각성', 'awaken2', ['superArmor']], [55, '백열참', 'active', ['superArmor']], [60, '진홍 월식', 'ultimate', ['superArmor']],
      [65, '천공 폭렬참', 'master', ['launcher']], [70, '혈룡 돌진', 'master', ['superArmor']], [75, '칼날 대폭풍', 'master', ['pull']],
      [80, '진홍 만월', 'master', ['projectile']], [85, '대지 단층', 'master', ['downHit']], [90, '공중 천무', 'master', ['air']], [95, '백열 만참', 'master', ['superArmor']],
    ]),
  },
  {
    id: 'lyra', name: '라이라', job: '궁수', weapon: '장궁', role: '원거리 단일·기동', colors: ['#3fd0c0', '#c8d2dc'],
    awaken: '폭풍 화살비', jobs: ['윈드 스나이퍼', '트랩 레인저'],
    skills: rows('lyra', [['차지샷', '관통 폭풍', '천공 저격'], ['지뢰밭', '덫 연쇄', '사냥꾼의 축제']], [
      [1, '3연사', 'base', ['projectile']], [1, '백스텝 사격', 'base', ['projectile', 'blink']], [1, '관통 화살', 'base', ['projectile']],
      [5, '올려쏘기', 'active', ['launcher', 'projectile']], [10, '바람 화살비', 'active', ['field']], [15, '공중 연사', 'active', ['air', 'projectile']],
      [20, '그물 덫', 'active', ['field']], [25, '폭발 화살', 'active', ['projectile']], [30, '전직 스킬 I', 'job', []],
      [35, '회오리 화살', 'active', ['pull', 'projectile']], [40, '전직 스킬 II', 'job', []], [45, '유도 화살 폭풍', 'active', ['projectile']],
      [50, '2차 각성', 'awaken2', []], [55, '천궁 강하', 'active', ['field']], [60, '신풍의 화살', 'ultimate', ['projectile']],
      [65, '폭풍 올려쏘기', 'master', ['launcher']], [70, '천공 화살비', 'master', ['field']], [75, '공중 섬광연사', 'master', ['air']],
      [80, '대폭발 화살', 'master', ['projectile']], [85, '태풍 화살', 'master', ['pull']], [90, '유성 화살', 'master', ['field']], [95, '사냥의 신', 'master', ['buff']],
    ]),
  },
  {
    id: 'seren', name: '세렌', job: '마법사', weapon: '지팡이', role: '광역·제어', colors: ['#ff8a3d', '#9fd8ff', '#b07cff'],
    awaken: '별자리 유성', jobs: ['원소술사', '시간술사'],
    skills: rows('seren', [['원소 융합', '화염 폭풍', '원소 대폭발'], ['시간 정지 장판', '되감기', '종말의 시계']], [
      [1, '마력탄', 'base', ['projectile']], [1, '화염구', 'base', ['projectile']], [1, '서리 노바', 'base', ['field']],
      [5, '염동 부양', 'active', ['launcher']], [10, '연쇄 번개', 'active', ['projectile']], [15, '공중 마력 연타', 'active', ['air']],
      [20, '블리자드', 'active', ['field']], [25, '블링크', 'active', ['blink']], [30, '전직 스킬 I', 'job', []],
      [35, '중력 구체', 'active', ['pull']], [40, '전직 스킬 II', 'job', []], [45, '메테오', 'active', ['field', 'downHit']],
      [50, '2차 각성', 'awaken2', []], [55, '원소 폭주', 'active', ['field']], [60, '차원 붕괴', 'ultimate', ['pull']],
      [65, '대염동', 'master', ['launcher']], [70, '천벌 연쇄뇌', 'master', ['projectile']], [75, '절대영도', 'master', ['field']],
      [80, '차원 도약', 'master', ['blink']], [85, '블랙홀', 'master', ['pull']], [90, '대메테오', 'master', ['field']], [95, '원소 천지', 'master', ['field']],
    ]),
  },
  {
    id: 'brann', name: '브란', job: '해머', weapon: '룬 해머', role: '탱커·지원', colors: ['#f2c94c', '#4a8cff'],
    awaken: '금빛 번개 낙뢰', jobs: ['수호 성기사', '파괴자'],
    skills: rows('brann', [['성역', '신성 일격', '심판의 망치'], ['대지 분쇄', '광폭 연타', '세계 파쇄']], [
      [1, '지면 강타', 'base', ['downHit']], [1, '어깨 돌진', 'base', ['superArmor']], [1, '도발 함성', 'base', ['buff']],
      [5, '해머 올려치기', 'active', ['launcher']], [10, '회전 해머', 'active', ['pull']], [15, '공중 내려찍기', 'active', ['air', 'downHit']],
      [20, '룬 보호막', 'active', ['buff']], [25, '잡아메치기', 'active', ['grab']], [30, '전직 스킬 I', 'job', []],
      [35, '천둥 망치 투척', 'active', ['projectile']], [40, '전직 스킬 II', 'job', []], [45, '지진', 'active', ['downHit', 'field']],
      [50, '2차 각성', 'awaken2', ['superArmor']], [55, '룬 거인화', 'active', ['superArmor', 'buff']], [60, '신들의 망치', 'ultimate', ['downHit']],
      [65, '천지 올려치기', 'master', ['launcher']], [70, '대회전 해머', 'master', ['pull']], [75, '성채 보호막', 'master', ['buff']],
      [80, '거인 메치기', 'master', ['grab']], [85, '뇌신 투척', 'master', ['projectile']], [90, '대지진', 'master', ['downHit']], [95, '룬 신화', 'master', ['buff']],
    ]),
  },
];

/** 레벨 L에서 습득한 스킬(자동 습득, PRD 3A.1) */
/** level까지 배운 스킬. job을 주면 그 갈래 전용 + 공통, 안 주면 갈래 전용은 빼고 공통만(전직 전) */
export function skillsAt(hero: HeroDef, level: number, job?: 0 | 1): SkillUnlock[] {
  return hero.skills.filter((s) => s.level <= level && (s.job === undefined || s.job === job));
}
/** 다음 새 스킬 레벨(HUD "다음 스킬까지"). 없으면 null */
export function nextSkillLevel(level: number): number | null {
  for (const l of SKILL_LEVELS) if (l > level) return l;
  return null;
}
